import { useState, useCallback, useRef } from "react";
import { LiveKitRoom, RoomAudioRenderer } from "@livekit/components-react";
import "@livekit/components-styles";
import { CallUI } from "../components/CallUI";
import "../styles/demo.css";

const BUILDER_EMAIL    = "omonswisdom.ict@gmail.com";
const BUILDER_WHATSAPP = "+2348141970770";
const BUILDER_NAME     = "Wisdom";

// ── Types ─────────────────────────────────────────────────────────────────────
type Stage = "form" | "connecting" | "call" | "ended";

interface FormState {
  email: string;
  businessName: string;
  city: string;
  ownerName: string;
  ownerMobile: string;
  country: string;
  postcodes: string;
  businessHours: string;
  serviceDescription: string;
  jobTypes: string[];
  customJobTypes: string;   // comma-separated free text
  escalationRules: string[];
}

interface Session {
  token: string;
  url: string;
  room: string;
  tenant: {
    business_name: string;
    city: string;
    country: string;
    owner_first_name: string;
    service_description: string;
    service_area: { postcode_prefixes: string[] };
    job_types: { name: string }[];
    _prospect_email: string;
  };
}

// ── Option lists ──────────────────────────────────────────────────────────────
const JOB_OPTIONS = [
  { key: "boiler_repair",      label: "Boiler repair" },
  { key: "boiler_service",     label: "Boiler service" },
  { key: "boiler_replacement", label: "Boiler replacement" },
  { key: "radiator_repair",    label: "Radiator repair" },
  { key: "leak",               label: "Leak / water leak" },
  { key: "blocked_drain",      label: "Blocked drain" },
  { key: "general_plumbing",   label: "General plumbing" },
  { key: "electrical_fault",   label: "Electrical fault" },
  { key: "roof_leak",          label: "Roof leak" },
  { key: "lockout",            label: "Lockout" },
];

const ESCALATION_OPTIONS = [
  { key: "gas_leak",              label: "Gas leak" },
  { key: "carbon_monoxide",       label: "Carbon monoxide" },
  { key: "flooding",              label: "Active flooding" },
  { key: "burst_pipe",            label: "Burst pipe" },
  { key: "uncontrolled_leak",     label: "Uncontrolled leak" },
  { key: "vulnerable_no_heating", label: "Vulnerable — no heating" },
  { key: "electrical_emergency",  label: "Electrical emergency" },
];

const COUNTRY_OPTIONS = [
  { value: "UK", label: "🇬🇧 United Kingdom" },
  { value: "US", label: "🇺🇸 United States" },
  { value: "CA", label: "🇨🇦 Canada" },
  { value: "NG", label: "🇳🇬 Nigeria" },
  { value: "OTHER", label: "🌍 Other" },
];

const AREA_HINT: Record<string, string> = {
  UK:    "UK postcode prefixes — e.g. LS17, M1, SW1A",
  US:    "Zip codes or city names — e.g. 90210, Austin TX",
  CA:    "Postal prefixes or cities — e.g. M5V, Vancouver",
  NG:    "Areas or LGAs — e.g. Lagos Island, Ikeja, Surulere",
  OTHER: "Any areas, cities or regions you cover",
};

const AREA_PLACEHOLDER: Record<string, string> = {
  UK:    "LS17, M1, M60",
  US:    "90210, 10001, Austin TX",
  CA:    "M5V, V6B, Toronto",
  NG:    "Lagos Island, Ikeja, VI",
  OTHER: "Your service area",
};

const DEFAULTS: FormState = {
  email: "",
  businessName: "", city: "", ownerName: "", ownerMobile: "",
  country: "UK",
  postcodes: "",
  businessHours: "9am to 5pm Monday to Friday, closed weekends",
  serviceDescription: "",
  jobTypes: ["boiler_repair", "boiler_service", "leak"],
  customJobTypes: "",
  escalationRules: ["gas_leak", "carbon_monoxide", "flooding", "burst_pipe"],
};

// ── Page ──────────────────────────────────────────────────────────────────────
export function DemoPage({ onBack }: { onBack: () => void }) {
  const [stage,   setStage]   = useState<Stage>("form");
  const [form,    setForm]    = useState<FormState>(DEFAULTS);
  const [session, setSession] = useState<Session | null>(null);
  const [error,   setError]   = useState("");
  const sessionRef  = useRef<Session | null>(null);
  const notifyRef   = useRef<object | null>(null);  // stores notify payload independently

  const submit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(""); setStage("connecting");
    try {
      const payload = {
        ...form,
        customJobTypes: form.customJobTypes
          .split(",")
          .map(s => s.trim())
          .filter(Boolean),
      };
      const r = await fetch("/api/demo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (!r.ok) {
        let msg = `Error ${r.status}`;
        if (d.detail) {
          if (typeof d.detail === "string") {
            msg = d.detail;
          } else if (Array.isArray(d.detail) && d.detail[0]?.msg) {
            msg = d.detail.map((e: { msg: string; loc?: string[] }) =>
              `${e.loc?.slice(-1)[0] ?? "field"}: ${e.msg}`
            ).join(". ");
          }
        } else if (d.error) {
          msg = d.error;
        }
        throw new Error(msg);
      }
      sessionRef.current = d;
      // Pre-build the notify payload immediately so handleCallEnd never has stale data
      notifyRef.current = {
        email:        d.tenant._prospect_email,
        ownerName:    d.tenant.owner_first_name,
        businessName: d.tenant.business_name,
        city:         d.tenant.city,
        postcodes:    d.tenant.service_area.postcode_prefixes,
        jobs:         d.tenant.job_types.map((j: { name: string }) => j.name),
      };
      setSession(d);
      setStage("call");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong.");
      setStage("form");
    }
  }, [form]);

  const handleCallEnd = useCallback(() => {
    setStage("ended");
    const payload = notifyRef.current;
    if (!payload) {
      console.warn("[notify] no payload, skipping");
      return;
    }
    console.log("[notify] firing", payload);
    fetch("/api/notify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then(r => r.json())
      .then(d => console.log("[notify] result:", d))
      .catch(err => console.error("[notify] failed:", err));
  }, []);

  return (
    <div className="demo-root">
      <div className="demo-glow" aria-hidden />
      <div className="demo-wrap">
        <div className="demo-topbar">
          <button className="demo-back" onClick={onBack}>← Back</button>
          <span className="demo-badge">Demo builder</span>
        </div>

        {stage === "form" && (
          <DemoForm form={form} setForm={setForm} onSubmit={submit} error={error} />
        )}

        {stage === "connecting" && (
          <div className="demo-connecting">
            <div className="demo-spinner" />
            <p>Spinning up your agent…</p>
          </div>
        )}

        {stage === "call" && session && (
          <div className="demo-call-wrap">
            <CallBadge tenant={session.tenant} />
            <LiveKitRoom
              token={session.token} serverUrl={session.url}
              connect audio video={false}
              onDisconnected={handleCallEnd}
              style={{ display: "contents" }}
            >
              <RoomAudioRenderer />
              <CallUI
                title={session.tenant.business_name}
                subtitle={`${session.tenant.city} · Demo`}
                onHangUp={handleCallEnd}
              />
            </LiveKitRoom>
          </div>
        )}

        {stage === "ended" && session && (
          <EndedScreen
            tenant={session.tenant}
            onReset={() => { setSession(null); setStage("form"); }}
          />
        )}
      </div>
    </div>
  );
}

// ── Form ──────────────────────────────────────────────────────────────────────
function DemoForm({ form, setForm, onSubmit, error }: {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  onSubmit: (e: React.FormEvent) => void;
  error: string;
}) {
  const str = (f: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm(p => ({ ...p, [f]: e.target.value }));

  const toggle = (f: "jobTypes" | "escalationRules", key: string) =>
    setForm(p => ({
      ...p,
      [f]: p[f].includes(key) ? p[f].filter(k => k !== key) : [...p[f], key],
    }));

  const areaHint        = AREA_HINT[form.country]        ?? AREA_HINT.OTHER;
  const areaPlaceholder = AREA_PLACEHOLDER[form.country] ?? AREA_PLACEHOLDER.OTHER;

  return (
    <form className="demo-form" onSubmit={onSubmit} noValidate>
      <div className="demo-form-intro">
        <h1 className="demo-h1">Build your demo</h1>
        <p className="demo-sub">
          Fill in your details and we'll spin up a call agent that knows your
          business in seconds. Call it like a customer would — then we'll send
          you a confirmation.
        </p>
      </div>

      {error && <div className="demo-error">{error}</div>}

      {/* Email */}
      <Section title="Your email">
        <Field label="Email address" hint="We'll send you a summary once the demo runs." required>
          <input className="inp" type="email" placeholder="you@yourbusiness.com"
            value={form.email} onChange={str("email")} required />
        </Field>
      </Section>

      {/* Business */}
      <Section title="Your business">
        <TwoCol>
          <Field label="Business name" required>
            <input className="inp" placeholder="Riverside Plumbing"
              value={form.businessName} onChange={str("businessName")} required />
          </Field>
          <Field label="City / town" required>
            <input className="inp" placeholder="Manchester"
              value={form.city} onChange={str("city")} required />
          </Field>
        </TwoCol>
        <TwoCol>
          <Field label="Your first name" required>
            <input className="inp" placeholder="Sarah"
              value={form.ownerName} onChange={str("ownerName")} required />
          </Field>
          <Field label="Mobile (for alerts)">
            <input className="inp" placeholder="+44 7700 900123"
              value={form.ownerMobile} onChange={str("ownerMobile")} />
          </Field>
        </TwoCol>
        <Field label="What service do you offer?" hint="e.g. plumbing, HVAC, electrical, locksmith, roofing">
          <input className="inp" placeholder="heating and plumbing"
            value={form.serviceDescription} onChange={str("serviceDescription")} />
        </Field>
        <Field label="Business hours">
          <input className="inp" value={form.businessHours} onChange={str("businessHours")} />
        </Field>
      </Section>

      {/* Country + Service area */}
      <Section title="Location">
        <Field label="Country">
          <select className="inp inp-select" value={form.country}
            onChange={str("country")}>
            {COUNTRY_OPTIONS.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Areas you cover" hint={areaHint} required>
          <input className="inp" placeholder={areaPlaceholder}
            value={form.postcodes} onChange={str("postcodes")} required />
        </Field>
      </Section>

      {/* Job types */}
      <Section title="Job types">
        <p className="section-hint">Pick what your business handles. Tick all that apply.</p>
        <CheckGrid>
          {JOB_OPTIONS.map(o => (
            <Chip key={o.key} label={o.label}
              checked={form.jobTypes.includes(o.key)}
              onChange={() => toggle("jobTypes", o.key)} />
          ))}
        </CheckGrid>
        <Field label="Custom job types" hint="Anything not listed above — separate with commas">
          <input className="inp" placeholder="e.g. HVAC service, pool repair, pest control"
            value={form.customJobTypes} onChange={str("customJobTypes")} />
        </Field>
      </Section>

      {/* Escalation rules */}
      <Section title="Escalation rules">
        <p className="section-hint">
          Which situations should the agent escalate to you immediately, without booking?
        </p>
        <CheckGrid>
          {ESCALATION_OPTIONS.map(o => (
            <Chip key={o.key} label={o.label}
              checked={form.escalationRules.includes(o.key)}
              onChange={() => toggle("escalationRules", o.key)} />
          ))}
        </CheckGrid>
      </Section>

      <button className="demo-submit" type="submit">
        Start demo call →
      </button>
      <p className="demo-legal">Demo uses in-memory calendar. No real bookings are made.</p>
    </form>
  );
}

// ── In-call badge ─────────────────────────────────────────────────────────────
function CallBadge({ tenant }: { tenant: Session["tenant"] }) {
  return (
    <div className="call-badge">
      <div className="call-badge-left">
        <span className="call-badge-icon">🔥</span>
        <div>
          <p className="call-badge-biz">{tenant.business_name}</p>
          <p className="call-badge-city">{tenant.city}</p>
        </div>
      </div>
      <div className="call-badge-right">
        {tenant.service_area.postcode_prefixes.slice(0, 4).join(", ")}
        {tenant.service_area.postcode_prefixes.length > 4 ? " …" : ""}
      </div>
    </div>
  );
}

// ── Post-call ended screen ────────────────────────────────────────────────────
function EndedScreen({ tenant, onReset }: { tenant: Session["tenant"]; onReset: () => void }) {
  const waMsg  = encodeURIComponent(
    `Hi ${BUILDER_NAME}, I just tried the HeatDesk demo for ${tenant.business_name} and want to go live.`
  );
  const waLink   = `https://wa.me/${BUILDER_WHATSAPP.replace("+", "")}?text=${waMsg}`;
  const mailLink = `mailto:${BUILDER_EMAIL}?subject=HeatDesk%20-%20Going%20live%20(${encodeURIComponent(tenant.business_name)})`;

  return (
    <div className="demo-ended">
      <div className="ended-check">✓</div>
      <h2 className="ended-title">Demo complete</h2>
      <p className="ended-body">
        That's how the agent sounds for <strong>{tenant.business_name}</strong>.
        It knew your area, your jobs and your name.
      </p>
      <p className="ended-email-note">
        Check your inbox — we've sent you a summary with next steps.
        <br />
        <span style={{ opacity: 0.7 }}>Not there? Check your spam folder.</span>
      </p>
      <div className="ended-actions">
        <a className="demo-submit ended-btn" href={mailLink}>
          Email {BUILDER_NAME} to go live
        </a>
        <a className="btn-wa" href={waLink} target="_blank" rel="noreferrer">
          <WaIcon /> WhatsApp {BUILDER_NAME}
        </a>
        <button className="btn-outline" onClick={onReset}>
          Try different details
        </button>
      </div>
    </div>
  );
}

// ── Small helpers ─────────────────────────────────────────────────────────────
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="demo-section">
      <legend className="demo-section-title">{title}</legend>
      {children}
    </fieldset>
  );
}
function TwoCol({ children }: { children: React.ReactNode }) {
  return <div className="two-col">{children}</div>;
}
function CheckGrid({ children }: { children: React.ReactNode }) {
  return <div className="check-grid">{children}</div>;
}
function Field({ label, hint, required, children }: {
  label: string; hint?: string; required?: boolean; children: React.ReactNode;
}) {
  return (
    <label className="demo-field">
      <span className="field-label">
        {label}{required && <span className="field-req"> *</span>}
      </span>
      {hint && <span className="field-hint">{hint}</span>}
      {children}
    </label>
  );
}
function Chip({ label, checked, onChange }: {
  label: string; checked: boolean; onChange: () => void;
}) {
  return (
    <label className={`chip${checked ? " chip-on" : ""}`}>
      <input type="checkbox" className="sr-only" checked={checked} onChange={onChange} />
      <span className="chip-dot" />
      {label}
    </label>
  );
}
function WaIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}
